import { getCookie, isLoggedOut } from "../Core/utils/cookieUtils";
import { getTokenObj, tokenIat, tokenUserId } from "./tokenManager";
import { AUTH_EVENTS, logAuthEvent } from "../utils/authEventLog";
import { FeatureFlag } from "../featureFlags/activeFlags";
import { FLAGS } from "../featureFlags/registry";

/**
 * Diagnostics for stale-tab recovery (auth_stale_tab_recovery; nothing is
 * added with the flag off). StudentPortal has the same module
 * (src/services/recoveryDiagnostics.js) with the same field names, so one
 * query covers both apps.
 *
 * Recovery logs a student out only when no store holds a newer pair than the
 * one cy-sso rejected. These helpers record what each store held at that
 * moment, and log the recoveries that worked, so the remaining logouts can be
 * told apart: nothing newer anywhere (the new pair never reached this
 * browser), or a newer pair the recovery did not use.
 *
 * Standalone only: embedded in the portal, Sandbox hands the refresh to the
 * portal's coordinator, which logs its own.
 *
 * Only labels and minutes leave the browser — never a token.
 */

const flagOn = () => FeatureFlag.isFeatureActive(FLAGS.AUTH_STALE_TAB_RECOVERY.name);

const minutesBetween = (laterSec, earlierSec) => Math.round((laterSec - earlierSec) / 60);

// The two places a token for this browser can be in standalone Sandbox: this
// origin's sandbox_user and the shared cookies (the portal's refresh lands here).
const STORES = {
  localStorage: () => ({ access: getTokenObj()?.ACCESS_TOKEN, refresh: true }),
  cookie: () => ({ access: getCookie("Authorization"), refresh: !!getCookie("refresh-token") }),
};

/**
 * How a store's token compares with the one that failed: "missing", "same",
 * "no-refresh-token", "other-user", "unreadable", "newer" or "not-newer";
 * aheadMin is how many minutes later (negative: earlier) it was issued.
 */
export const compareStoreToken = ({ access, refresh }, failedToken) => {
  if (!access) return { state: "missing", aheadMin: null };
  if (access === failedToken) return { state: "same", aheadMin: 0 };
  if (!refresh) return { state: "no-refresh-token", aheadMin: null };
  if (tokenUserId(access) !== tokenUserId(failedToken)) return { state: "other-user", aheadMin: null };
  const found = tokenIat(access);
  const failed = tokenIat(failedToken);
  if (found === null || failed === null) return { state: "unreadable", aheadMin: null };
  return { state: found > failed ? "newer" : "not-newer", aheadMin: minutesBetween(found, failed) };
};

const tokenAgeMin = (token) => {
  const iat = tokenIat(token);
  return iat === null ? null : minutesBetween(Date.now() / 1000, iat);
};

/**
 * Fields for the refresh-failed log: what every store held when recovery gave
 * up, how old the rejected token was and how long the page had been open.
 * Empty with the flag off. Never throws.
 */
export const recoveryCheckDetails = (failedToken, { adoptedBeforeRefresh = false } = {}) => {
  try {
    if (!flagOn()) return {};
    const stores = {};
    Object.entries(STORES).forEach(([name, read]) => {
      try {
        stores[name] = compareStoreToken(read(), failedToken);
      } catch (_) {
        stores[name] = { state: "unreadable", aheadMin: null };
      }
    });
    return {
      recoveryCheck: {
        stores,
        adoptedBeforeRefresh,
        failedTokenAgeMin: tokenAgeMin(failedToken),
        pageOpenMin: Math.round(performance.now() / 60000),
        tabVisible: document.visibilityState === "visible",
        loggedOutFlag: isLoggedOut(),
      },
    };
  } catch (_) {
    return {};
  }
};

/**
 * Logs a recovery that kept the student signed in — one event per recovery
 * (a burst of 401s shares one refresh, and the focus resync logs only when it
 * adopts a newer pair), so the counts are exact.
 *   stage:  "before-refresh" | "after-reject" | "race-lost" | "focus-resync"
 *   source: the store the newer pair came from ("localStorage" | "cookie")
 * No-op with the flag off. Never throws.
 */
export const logSessionRecovered = ({ stage, source, refreshPath, staleToken }) => {
  try {
    if (!flagOn()) return;
    logAuthEvent(AUTH_EVENTS.SESSION_RECOVERED, {
      stage,
      source: source || null,
      refreshPath: refreshPath || null,
      staleTokenAgeMin: staleToken ? tokenAgeMin(staleToken) : null,
    });
  } catch (_) {
    /* never block a recovery */
  }
};
